import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, MapPin, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { submitMapCorrection, CorrectionType } from '../services/mapCorrectionService';
import { Coordinates } from '../types';

interface MapCorrectionModalProps {
  coords: Coordinates;
  userId: string;
  onClose: () => void;
}

const CORRECTION_OPTIONS: { value: CorrectionType; labelKey: string; icon: string }[] = [
  { value: 'innmark_as_utmark', labelKey: 'map_correction.type_innmark_as_utmark', icon: '🏠→🌲' },
  { value: 'utmark_as_innmark', labelKey: 'map_correction.type_utmark_as_innmark', icon: '🌲→🏠' },
  { value: 'other', labelKey: 'map_correction.type_other', icon: '❓' },
];

const MapCorrectionModal: React.FC<MapCorrectionModalProps> = ({ coords, userId, onClose }) => {
  const { t } = useTranslation();
  const [correctionType, setCorrectionType] = useState<CorrectionType | null>(null);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!correctionType) return;
    setSending(true);
    setError(null);
    try {
      await submitMapCorrection({
        userId,
        latitude: coords.latitude,
        longitude: coords.longitude,
        correctionType,
        comment: comment.trim()
      });
      setSent(true);
    } catch (e: any) {
      setError(e.message || t('map_correction.error'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md mx-4 mb-4 sm:mb-0 bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 max-h-[90dvh] flex flex-col" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-secondary)]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
              <AlertTriangle size={16} className="text-amber-400" />
            </div>
            <div>
              <p className="text-sm font-bold text-[var(--text-primary)]">{t('map_correction.title')}</p>
              <p className="text-2xs text-[var(--text-muted)]">
                {coords.latitude.toFixed(5)}, {coords.longitude.toFixed(5)}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-[var(--bg-surface)] transition-colors">
            <X size={16} className="text-[var(--text-secondary)]" />
          </button>
        </div>

        {sent ? (
          /* Success */
          <div className="p-6 text-center space-y-3">
            <CheckCircle2 size={40} className="text-emerald-400 mx-auto" />
            <p className="text-sm font-bold text-[var(--text-primary)]">{t('map_correction.thanks_title')}</p>
            <p className="text-xs text-[var(--text-secondary)]">{t('map_correction.thanks_body')}</p>
            <button onClick={onClose} className="mt-2 px-6 py-2.5 bg-emerald-600 text-white rounded-xl text-2xs font-black uppercase tracking-widest hover:bg-emerald-500 transition-all">
              {t('common.close')}
            </button>
          </div>
        ) : (
          /* Form */
          <div className="p-5 space-y-4 overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
            <p className="text-xs text-[var(--text-secondary)] leading-relaxed">{t('map_correction.description')}</p>

            {/* Correction type */}
            <div className="space-y-2">
              <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">{t('map_correction.what_is_wrong')}</p>
              {CORRECTION_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setCorrectionType(opt.value)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                    correctionType === opt.value
                      ? 'bg-amber-500/15 border border-amber-500/30 text-amber-300'
                      : 'bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] text-[var(--text-secondary)] hover:bg-[var(--bg-surface)]'
                  }`}
                >
                  <span className="text-base">{opt.icon}</span>
                  <span className="text-xs font-bold">{t(opt.labelKey)}</span>
                </button>
              ))}
            </div>

            {/* Comment */}
            <div className="space-y-1">
              <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">{t('map_correction.comment_label')}</p>
              <textarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder={t('map_correction.comment_placeholder')}
                rows={3}
                className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-4 py-3 text-[var(--text-primary)] text-xs focus:border-amber-500/50 outline-none resize-none leading-relaxed"
              />
            </div>

            {error && <p className="text-tiny text-red-400">{error}</p>}

            <button
              onClick={handleSubmit}
              disabled={!correctionType || sending}
              className="w-full py-3 bg-amber-600 hover:bg-amber-500 disabled:opacity-40 text-white rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-95"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <MapPin size={14} />}
              {t('map_correction.submit')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MapCorrectionModal;
